




import { Component, Mixins } from "vue-property-decorator";
import { EditingDocument } from "..";
import { IDocumentImageSource, ImageKinds } from "../../../../domain/document";
import { ImageItem } from "../../../../domain/document/items";
import { ModelEditableMixin } from "../../../system";

class ImageInformation {
  public imageSource: IDocumentImageSource;
  public originalImageSource: IDocumentImageSource;
  public drawLayerImageSource: IDocumentImageSource;
  public drawObjectData: string;
  public imageKind: ImageKinds;
  public claioImageId: string;
  public rotationAngle: number;
  public imageDepartmentId: string;
  public externalImageId: string;
}

@Component
export default class DocumentImageSwapper extends Mixins(ModelEditableMixin) {
  writePropertyWhenValid: (writer: (source: EditingDocument) => void) => void;

  public getCurrentSectionItemSetId(): number {
    return this.readPropertyOrDefault((d: EditingDocument) => d.currentSection.itemSetId, null);
  }

  public swap(itemSetId: number, sourceObjectPrivateId: number, targetObjectPrivateId: number): void {
    if (itemSetId == null) { throw new Error("itemSetId is invalid!!!"); }
    if (!this.isImageObject(itemSetId, sourceObjectPrivateId) || !this.isImageObject(itemSetId, targetObjectPrivateId)) {
      this.$warn("画像入れ替えチェック", "画像入れ替え対象外のオブジェクトです。");
      return;
    }
    try {
      this.writePropertyWhenValid(d => this.swapImageInformation(d, itemSetId, sourceObjectPrivateId, targetObjectPrivateId));
      this.$nextTick(() => {
        this.executeStackedScriptsAsync()
        .catch(e => {
          this.$error("エラー", "外部スクリプトの実行でエラーが発生しました。", e);
        });
      });
    } catch (e) {
      this.$warn("画像入れ替えエラー", "画像入れ替えの処理中にエラーが発生しました。", e);
    }
  }

  private async executeStackedScriptsAsync(): Promise<void> {
    const result = await (this.editingSource as EditingDocument).executeStackedScriptsAsyncWithResult();
    if (!result) {
      throw new Error("外部スクリプトの実行中にエラーが発生しています。");
    }
  }

  private isImageObject(itemSetId: number, privateId: number): boolean {
    const item = this.readPropertyOrDefault((d: EditingDocument) => d.itemSets[itemSetId].items[privateId], null);
    return !!item && (item instanceof ImageItem);
  }

  private swapImageInformation(editingDocument: EditingDocument, itemSetId: number, sourceObjectPrivateId: number, targetObjectPrivateId: number): void {
    const sourceImageInformation = this.getImageInformation(editingDocument, itemSetId, sourceObjectPrivateId);
    const targetImageInformation = this.getImageInformation(editingDocument, itemSetId, targetObjectPrivateId);
    this.applyImageInformation(editingDocument, itemSetId, sourceObjectPrivateId, targetImageInformation);
    this.applyImageInformation(editingDocument, itemSetId, targetObjectPrivateId, sourceImageInformation);
  }

  private getImageInformation(editingDocument: EditingDocument, itemSetId: number, privateId: number): ImageInformation {
    const item = editingDocument.itemSets[itemSetId].items[privateId] as ImageItem;
    const imageInfo = new ImageInformation();
    imageInfo.imageSource = this.cloneDocumentImageSource(item.imageSource);
    imageInfo.originalImageSource = this.cloneDocumentImageSource(item.originalImageSource);
    imageInfo.drawLayerImageSource = this.cloneDocumentImageSource(item.drawLayerImageSource);
    imageInfo.drawObjectData = item.drawObjectData;
    imageInfo.imageKind = !!item.imageKind ? item.imageKind : ImageKinds.Image;
    imageInfo.claioImageId = item.claioImageId;
    imageInfo.rotationAngle = item.rotationAngle;
    imageInfo.imageDepartmentId = item.imageDepartmentId;
    imageInfo.externalImageId = item.externalImageId;
    return imageInfo;
  }

  private cloneDocumentImageSource(source: IDocumentImageSource): IDocumentImageSource {
    return !!source
      ? source.clone() as IDocumentImageSource
      : null;
  }

  private applyImageInformation(editingDocument: EditingDocument, itemSetId: number, privateId: number, imageInfo: ImageInformation): void {
    const item = editingDocument.itemSets[itemSetId].items[privateId];
    const cloned = item.clone() as ImageItem;
    cloned.clearImageInformation();
    cloned.imageSource = imageInfo.imageSource;
    cloned.originalImageSource = imageInfo.originalImageSource;
    cloned.drawLayerImageSource = imageInfo.drawLayerImageSource;
    cloned.drawObjectData = imageInfo.drawObjectData;
    cloned.imageKind = imageInfo.imageKind;
    cloned.claioImageId = imageInfo.claioImageId;
    cloned.rotationAngle = imageInfo.rotationAngle;
    cloned.imageDepartmentId = imageInfo.imageDepartmentId;
    cloned.externalImageId = imageInfo.externalImageId;
    editingDocument.updateItem(cloned, {
      itemSetId,
    });
  }
}
